import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

import ANLogoSrc from "assets/answers_now_logo_dark_purple.png";
import ANLogoSmallSrc from "assets/an_purple_logo.png";

const VERSION = process.env.APP_VERSION || "";

type SplashScreenProps = {
  alertSeverity?: "error" | "warning" | "info" | "success";
  description: React.ReactNode;
  title: string;
};

/**
 * The SplashScreen function renders a component with the ANlogo, alert message, title, and description in
 * a centered layout.
 * @component
 * @param {Object} props The props of the SplashScreen component
 * @param {string} [props.alertSeverity="info"] The severity of the alert message (error, warning, info, success).
 * @param {React.ReactNode} props.description The description of the alert message.
 * @param {string} props.title The title of the alert message.
 * @returns {JSX.Element} The SplashScreen component
 * @example
 * <SplashScreen alertSeverity="error" description="An error occurred" title="Error" />
 */
export default function SplashScreen({
  alertSeverity = "info",
  description,
  title,
}: SplashScreenProps) {
  const { t } = useTranslation(["auth"]);
  const theme = useTheme();
  const sm = theme.breakpoints.values.sm;

  return (
    <Stack
      id="splash-screen"
      direction="column"
      height="100dvh"
      alignItems="center"
      justifyContent="space-between"
    >
      <Box flex={"1 1 auto"} />

      <Box flex={"1 1 auto"}>
        <Stack maxWidth="330px" alignItems="center" gap={11}>
          <picture>
            <source media={`(width < ${sm}px)`} srcSet={ANLogoSmallSrc} />
            <source media={`(width >= ${sm}px)`} srcSet={ANLogoSrc} />
            <img src={ANLogoSrc} alt="AnswersNow" title="AnswersNow" height={48} />
          </picture>

          <Stack alignItems="center" gap={4}>
            <Alert severity={alertSeverity}>
              <AlertTitle>{title}</AlertTitle>
              <Typography component="p" variant="body2">
                {description}
              </Typography>
            </Alert>
          </Stack>
        </Stack>
      </Box>

      <Box flex={"0 0 auto"} px={4} pb={2}>
        <Typography component="p" variant="caption" color="text.secondary" textAlign="center">
          {t("auth:resetPassword.copyright", { version: VERSION, year: new Date().getFullYear() })}
        </Typography>
      </Box>
    </Stack>
  );
}
